// web/src/shell/TopBar.tsx — the app shell's top bar (M0.4). Left: a global
// search / command-palette placeholder (⌘K — wired in M4.4). Right: realtime
// status, the act-as identity switcher (stub, D-013), an environment chip, a
// theme toggle, and the identity + role badge. Reads shell-level state from the
// ShellContext. Token-only styling in shell.css.
import React from 'react';
import { Search, Sun, Moon } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { useShell } from './ShellContext';
import { TrustIndicator } from './TrustIndicator';
import { STUB_PRINCIPALS, type ActAsId } from '../auth';

export function TopBar(): React.JSX.Element {
  const { principal, theme, toggleTheme, actAs, switchActAs, wsConnected, environment, openPalette } =
    useShell();

  return (
    <header className="bgTopBar">
      {/* Opens the ⌘K command palette (M4.4). */}
      <button
        type="button"
        className="bgSearch"
        aria-label="Search and commands (Command or Control K)"
        onClick={() => openPalette?.()}
      >
        <Icon icon={Search} size="sm" tone="subtle" />
        <span className="bgSearchText">Search or run a command</span>
        <kbd className="bgKbd">⌘K</kbd>
      </button>

      <div className="bgTopRight">
        <TrustIndicator />

        <span className="bgWs" role="status">
          <span
            className={`bgWsDot ${wsConnected ? 'bgWsOn' : 'bgWsOff'}`}
            aria-label={wsConnected ? 'Realtime connected' : 'Realtime reconnecting'}
          />
          {wsConnected ? 'Live' : 'Reconnecting'}
        </span>

        <span
          className="bgEnvChip"
          title={`Environment: ${environment}`}
          data-env={environment}
        >
          {environment}
        </span>

        <div className="bgSeg" role="group" aria-label="Acting as">
          <span className="bgSegLabel">Acting as</span>
          {(Object.keys(STUB_PRINCIPALS) as ActAsId[]).map((id) => (
            <button
              key={id}
              type="button"
              className={`bgSegBtn ${actAs === id ? 'bgSegBtnActive' : ''}`}
              aria-pressed={actAs === id}
              onClick={() => switchActAs(id)}
              title={`${STUB_PRINCIPALS[id].name} (${STUB_PRINCIPALS[id].role})`}
            >
              {STUB_PRINCIPALS[id].name.split(' ')[0]}
            </button>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={toggleTheme}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        >
          <Icon icon={theme === 'dark' ? Sun : Moon} size="sm" />
        </button>

        <div className="bgPrincipal">
          <span className="bgPrincipalName">{principal.name}</span>
          <span className="bgPrincipalRole">{principal.role}</span>
        </div>
      </div>
    </header>
  );
}

export default TopBar;
